import React from 'react';
import { observer } from 'mobx-react';
import CommanderCard from './CommanderCard';
import styles from './CommanderCardsGrid.css';

export interface CommanderDamage {
    isMe: boolean;
    damage: number;
    opponentId?: string;
    backgroundColor: string;
}

export interface CommanderCardsGridProps {
    commanderDamage: CommanderDamage[];
    onIncrease: (opponentId: string, amount: number) => void;
    onDecrease: (opponentId: string, amount: number) => void;
    onIncreaseDeaths?: () => void;
    onDecreaseDeaths?: () => void;
    isBig?: boolean;
    playerRotation?: number;
    commanderDeaths?: number;
}

const CommanderCardsGrid = observer(({
    commanderDamage,
    onIncrease,
    onDecrease,
    onIncreaseDeaths,
    onDecreaseDeaths,
    isBig = false,
    playerRotation = 0,
    commanderDeaths = 0,
}: CommanderCardsGridProps) => {
    const getReorderedCards = () => {
        const cards = [...commanderDamage];

        if (playerRotation === 0 || cards.length <= 1) {
            return cards;
        }

        switch (playerRotation) {
            case 180:
                return [...cards].reverse();

            case 90:
                if (cards.length === 4) {
                    return [cards[2], cards[3], cards[1], cards[0]];
                } else if (cards.length === 3) {
                    return [cards[1], cards[2], cards[0]];
                } else {
                    const result = [...cards];
                    result.push(result.shift()!);
                    return result;
                }

            case 270:
                if (cards.length === 4) {
                    return [cards[0], cards[1], cards[3], cards[2]];
                } else if (cards.length === 3) {
                    return [cards[2], cards[0], cards[1]];
                } else {
                    const result = [...cards];
                    result.unshift(result.pop()!);
                    return result;
                }

            default:
                return cards;
        }
    };

    const orderedCards = getReorderedCards();
    const count = orderedCards.length;

    const renderCard = (data: CommanderDamage, index: number) => (
        <CommanderCard
            key={data.opponentId || index}
            data={data}
            onIncrease={onIncrease}
            onDecrease={onDecrease}
            onIncreaseDeaths={onIncreaseDeaths}
            onDecreaseDeaths={onDecreaseDeaths}
            isBig={isBig}
            commanderDeaths={commanderDeaths}
            cardIndex={index}
        />
    );

    let layout;

    switch (count) {
        case 2:
            layout = (
                <>
                    <div className={`${styles.row} ${isBig ? styles.rowBig : ''}`}>
                        <div style={{ flex: 1, display: 'flex', height: '100%' }}>{renderCard(orderedCards[0], 0)}</div>
                    </div>
                    <div className={`${styles.row} ${isBig ? styles.rowBig : ''}`}>
                        <div style={{ flex: 1, display: 'flex', height: '100%' }}>{renderCard(orderedCards[1], 1)}</div>
                    </div>
                </>
            );
            break;
        case 3:
            layout = (
                <div className={`${styles.row} ${isBig ? styles.rowBig : ''}`} style={{ height: '100%' }}>
                    <div style={{ flex: 1, display: 'flex', height: '100%' }}>{renderCard(orderedCards[0], 0)}</div>
                    <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: isBig ? 12 : 4, height: '100%' }}>
                        <div style={{ flex: 1, display: 'flex' }}>{renderCard(orderedCards[1], 1)}</div>
                        <div style={{ flex: 1, display: 'flex' }}>{renderCard(orderedCards[2], 2)}</div>
                    </div>
                </div>
            );
            break;
        case 4:
            layout = (
                <>
                    <div className={`${styles.row} ${isBig ? styles.rowBig : ''}`}>
                        {orderedCards.slice(0, 2).map((cd, index) => (
                            <div style={{ flex: 1, display: 'flex', height: '100%' }} key={cd.opponentId || index}>
                                {renderCard(cd, index)}
                            </div>
                        ))}
                    </div>
                    <div className={`${styles.row} ${isBig ? styles.rowBig : ''}`}>
                        {orderedCards.slice(2, 4).map((cd, index) => (
                            <div style={{ flex: 1, display: 'flex', height: '100%' }} key={cd.opponentId || index}>
                                {renderCard(cd, index + 2)}
                            </div>
                        ))}
                    </div>
                </>
            );
            break;
        default:
            layout = (
                <div className={`${styles.row} ${isBig ? styles.rowBig : ''}`}>
                    {orderedCards.map((cd, index) => (
                        <div style={{ flex: 1, display: 'flex', height: '100%' }} key={cd.opponentId || index}>
                            {renderCard(cd, index)}
                        </div>
                    ))}
                </div>
            );
    }

    return (
        <div className={`${styles.container} ${isBig ? styles.containerBig : ''}`}>
            {layout}
        </div>
    );
});

export default CommanderCardsGrid;
